import { Extension } from '@tiptap/core'
import Suggestion from '@tiptap/suggestion'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'

// Slash Menu Extension for Notion-like editor
export const SlashMenuExtension = Extension.create({
  name: 'slashMenu',

  addOptions() {
    return {
      mode: 'html',  // 'html' or 'markdown'
      suggestion: {
        char: '/',
        startOfLine: false,

        command: ({ editor, range, props }) => {
          // 執行選中的指令
          props.command({ editor, range })
        },

        items: ({ query, editor }) => {
          const mode = editor.storage.slashMenu?.mode || 'html'
          const commands = getCommandsForMode(mode)

          return commands
            .filter(item => item.title.toLowerCase().includes(query.toLowerCase()))
            .slice(0, 10)
        },

        render: () => {
          let component
          let popup

          return {
            onStart: props => {
              component = new SlashMenuList(props)

              if (!props.clientRect) {
                return
              }

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
                theme: 'slash-menu',
                maxWidth: 'none',
              })
            },

            onUpdate(props) {
              component.updateProps(props)

              if (!props.clientRect) {
                return
              }

              popup[0].setProps({
                getReferenceClientRect: props.clientRect,
              })
            },

            onKeyDown(props) {
              if (props.event.key === 'Escape') {
                popup[0].hide()
                return true
              }

              return component.onKeyDown(props)
            },

            onExit() {
              popup[0].destroy()
              component.destroy()
            },
          }
        },
      },
    }
  },

  addStorage() {
    return {
      mode: this.options.mode,
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})

// Slash Menu UI Component
class SlashMenuList {
  constructor(props) {
    this.props = props
    this.selectedIndex = 0

    this.element = document.createElement('div')
    this.element.className = 'slash-menu'

    this.render()
  }

  render() {
    const { items } = this.props

    if (!items || items.length === 0) {
      this.element.innerHTML = '<div class="slash-menu-item empty">找不到指令</div>'
      return
    }

    this.element.innerHTML = items
      .map((item, index) => {
        const isSelected = index === this.selectedIndex
        return `
          <button
            type="button"
            class="slash-menu-item ${isSelected ? 'selected' : ''}"
            data-index="${index}"
          >
            <div class="slash-menu-item-icon">${item.icon || '📝'}</div>
            <div class="slash-menu-item-content">
              <div class="slash-menu-item-title">${item.title}</div>
              <div class="slash-menu-item-description">${item.description || ''}</div>
            </div>
          </button>
        `
      })
      .join('')

    // Add click handlers (重新綁定事件)
    this.element.querySelectorAll('.slash-menu-item').forEach((el, index) => {
      el.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.selectItem(index)
      })
    })
  }

  updateProps(props) {
    this.props = props
    this.selectedIndex = 0  // 重設選擇
    this.render()
  }

  onKeyDown({ event }) {
    if (event.key === 'ArrowUp') {
      this.upHandler()
      return true
    }

    if (event.key === 'ArrowDown') {
      this.downHandler()
      return true
    }

    if (event.key === 'Enter') {
      this.enterHandler()
      return true
    }

    return false
  }

  upHandler() {
    this.selectedIndex = ((this.selectedIndex + this.props.items.length) - 1) % this.props.items.length
    this.render()
  }

  downHandler() {
    this.selectedIndex = (this.selectedIndex + 1) % this.props.items.length
    this.render()
  }

  enterHandler() {
    this.selectItem(this.selectedIndex)
  }

  selectItem(index) {
    const item = this.props.items[index]

    if (item && item.command) {
      // 直接執行 item 的 command
      item.command({
        editor: this.props.editor,
        range: this.props.range,
      })
    }
  }

  destroy() {
    this.element.remove()
  }
}

// Commands for different modes
function getCommandsForMode(mode) {
  const htmlCommands = [
    {
      title: 'Heading 1 (H1)',
      description: '大標題',
      icon: 'H1',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 1 })
          .run()
      }
    },
    {
      title: 'Heading 2 (H2)',
      description: '中標題',
      icon: 'H2',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 2 })
          .run()
      }
    },
    {
      title: 'Heading 3 (H3)',
      description: '小標題',
      icon: 'H3',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 3 })
          .run()
      }
    },
    {
      title: 'Heading 4 (H4)',
      description: '次標題',
      icon: 'H4',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode('heading', { level: 4 })
          .run()
      }
    },
    {
      title: 'Paragraph',
      description: '一般段落',
      icon: '¶',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setParagraph()
          .run()
      }
    },
    {
      title: 'Bullet List',
      description: '無序列表',
      icon: '•',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBulletList()
          .run()
      }
    },
    {
      title: 'Numbered List',
      description: '有序列表',
      icon: '1.',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleOrderedList()
          .run()
      }
    },
    {
      title: 'Task List',
      description: '待辦事項',
      icon: '☑',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleTaskList()
          .run()
      }
    },
    {
      title: 'Blockquote',
      description: '引用區塊',
      icon: '❝',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleBlockquote()
          .run()
      }
    },
    {
      title: 'Code Block',
      description: '程式碼區塊',
      icon: '</>',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleCodeBlock()
          .run()
      }
    },
    {
      title: 'Horizontal Rule',
      description: '分隔線',
      icon: '—',
      command: ({ editor, range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setHorizontalRule()
          .run()
      }
    },
  ]

  // HTML 模式才有的指令
  if (mode === 'html') {
    htmlCommands.push(
      {
        title: 'Table',
        description: '插入表格',
        icon: '⊞',
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
      }
    )
  }

  const markdownCommands = htmlCommands.filter(cmd => {
    // Markdown 模式排除 Table
    return cmd.title !== 'Table'
  })

  return mode === 'html' ? htmlCommands : markdownCommands
}
